import React, { useState, useEffect, useContext } from 'react';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import isWeekend from 'date-fns/isWeekend';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Select from 'react-select';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { FaUsers, FaCalendarAlt } from "react-icons/fa";
import Loader from "../components/common/Loader";
import API_BASE_URLS from "../config/api";

const locales = {
  'en-US': require('date-fns/locale/en-US'),
};
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const API_BASE_URL = `${API_BASE_URLS}/api/Event`;

const EVENT_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A',
  '#98D8C8', '#F06292', '#7986CB', '#9575CD',
  '#64B5F6', '#4DB6AC', '#81C784', '#FFD54F'
];

const formatForDateTimeInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};


const ResponsiveCalendar = () => {
  const [userRole, setUserRole] = useState(null);
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: new Date(),
    end: new Date(),
    color: EVENT_COLORS[Math.floor(Math.random() * EVENT_COLORS.length)],
    type: "event",
    participants: []
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [highlightWeekends, setHighlightWeekends] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [employees, setEmployees] = useState([]);
  const selectAllOption = { value: "all", label: "Select All" };
  const allOptions = [selectAllOption, ...employees];
  const [notification, setNotification] = useState(null);

  const employeeCode = sessionStorage.getItem("username");
  const { token } = useContext(AuthContext);

  const [recurrenceDays, setRecurrenceDays] = useState([]);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState(1);

  useEffect(() => {
    const fetchEmployees = async () => {
      const res = await axios.get(`${API_BASE_URLS}/api/employee`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setEmployees(res.data.map((emp) => ({ value: emp.employeeID, label: emp.firstName + ' ' + emp.lastName })));
    };
    fetchEmployees();
  }, [token]);

  const EventWithIcon = ({ event }) => {
    let Icon;

    if (event.type?.toLowerCase() === "meeting") {
      Icon = FaUsers;
    } else {
      Icon = FaCalendarAlt;
    }

    return (
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Icon style={{ fontSize: "14px", color: "#fff" }} />
        <span>{event.title}</span>
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const role = sessionStorage.getItem("userrole");
    setUserRole(role);
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BASE_URL);
      const data = res.data;

      const mappedEvents = data
        .filter(e =>
          userRole === "1" ||
          !e.participants ||
          e.participants === "" ||
          e.participants.split(',').includes(employeeCode) ||
          e.createdBy === employeeCode
        )
        .map(e => {
          const start = e.startDate ? new Date(e.startDate) : new Date();
          const end = e.endDate
            ? new Date(e.endDate)
            : new Date(start.getTime() + 60 * 60 * 1000);

          return {
            id: e.id,
            title: e.title || "Untitled",
            start,
            end,
            color: e.color || EVENT_COLORS[Math.floor(Math.random() * EVENT_COLORS.length)],
            createdBy: e.createdBy,
            type: e.type,
            participants: e.participants
              ? e.participants.split(",").map(code => code.trim())
              : []
          };
        })

      setEvents(mappedEvents);
      
    } catch (err) {
      console.error("Failed to load events:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetNewEvent = () => {
    setNewEvent({
      title: '',
      start: new Date(),
      end: new Date(),
      color: EVENT_COLORS[Math.floor(Math.random() * EVENT_COLORS.length)],
      type: "event",
      participants: []
    });
    setSelectedEvent(null);
    setRecurrenceDays([]);
    setRecurrenceFrequency(1);
  };

  const navigate = (action) => {
    const newDate = new Date(date);
    switch (action) {
      case 'PREV':
        if (view === Views.YEAR) newDate.setFullYear(date.getFullYear() - 1);
        else if (view === Views.MONTH) newDate.setMonth(date.getMonth() - 1);
        else newDate.setDate(date.getDate() - 1);
        break;
      case 'NEXT':
        if (view === Views.YEAR) newDate.setFullYear(date.getFullYear() + 1);
        else if (view === Views.MONTH) newDate.setMonth(date.getMonth() + 1);
        else newDate.setDate(date.getDate() + 1);
        break;
      case 'TODAY':
        setDate(new Date());
        return;
      default:
        break;
    }
    setDate(newDate);
  };

  const formatDateRange = ({ start }, culture, localizer) => {
    if (view === Views.YEAR) return localizer.format(start, 'yyyy', culture);
    if (view === Views.MONTH) return localizer.format(start, 'MMMM yyyy', culture);
    return localizer.format(start, 'PPPP', culture);
  };

  const toggleRecurrenceDay = (dayIdx) => {
    setRecurrenceDays(prev =>
      prev.includes(dayIdx)
        ? prev.filter(d => d !== dayIdx)
        : [...prev, dayIdx]
    );
  };

  function getRecurringDates(startDate, endDate, recurrenceDays, frequencyInWeeks, weeksInYear = 52) {
    const events = [];
    const year = startDate.getFullYear();
    const firstWeekStart = new Date(startDate);
    firstWeekStart.setDate(startDate.getDate() - startDate.getDay());

    const eventDuration = endDate.getTime() - startDate.getTime(); // duration in ms

    for (let week = 0; week < weeksInYear; week += frequencyInWeeks) {
      recurrenceDays.forEach(day => {
        const start = new Date(firstWeekStart);
        start.setDate(firstWeekStart.getDate() + week * 7 + day);
        start.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);

        const end = new Date(start.getTime() + eventDuration);

        if (start.getFullYear() === year && start >= startDate) {
          events.push({ startDate: start, endDate: end });
        }
      });
    }

    events.sort((a, b) => a.startDate - b.startDate);
    return events;
  }

  const handleSelectSlot = (slotInfo) => {
    if (userRole !== "1") return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Prevent creating events in the past
    if (slotInfo.start < today) {
      return;
    }
    setNewEvent({
      title: '',
      start: slotInfo.start,
      end: slotInfo.end,
      color: EVENT_COLORS[Math.floor(Math.random() * EVENT_COLORS.length)],
      type: slotInfo.type,
      participants: slotInfo.participants ? slotInfo.participants.split(',').map(code => code.trim()) : []
    });
    setSelectedEvent(null);
    resetRecurrence();
    setShowModal(true);
    closeNotification();
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isPastEvent = new Date(event.end) < today;

    if (userRole === "1" && event.createdBy === employeeCode && !isPastEvent) {

      const participants = typeof event.participants === 'string'
        ? event.participants.split(',').map(code => code.trim())
        : Array.isArray(event.participants)
          ? event.participants
          : [];

      setNewEvent({
        title: event.title,
        start: new Date(event.start),
        end: new Date(event.end),
        id: event.id,
        color: event.color,
        createdBy: event.createdBy,
        type: event.type,
        participants: participants
      });
      setShowModal(true);
    } else {
      setShowDetailsModal(true);
    }
  };

  const resetRecurrence = () => {
    setRecurrenceDays([]);
    setRecurrenceFrequency(1);
  };

  const validateDates = () => {
    const now = new Date();

    if (!newEvent.start || !newEvent.end) {
      return "Start and End date are required.";
    }
    if (newEvent.start < now) {
      return "Start date/time cannot be in the past.";
    }
    if (newEvent.end <= newEvent.start) {
      return "End date/time must be later than Start date/time.";
    }
    return null;
  };


  const handleAddEvent = async () => {
    if (!newEvent.title.trim()) {
      setNotification({ type: "error", message: "Event title is required." });
      return;
    }
    const error = validateDates();
    if (error) {
      setNotification({ type: "error", message: error });
      return;
    }
    try {
      if (recurrenceDays.length === 0) {
        await axios.post(API_BASE_URL, {
          title: newEvent.title,
          startDate: newEvent.start.toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
          endDate: newEvent.end.toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
          color: newEvent.color,
          createdBy: employeeCode,
          type: newEvent.type,
          participants: newEvent.participants.join(',')
        });
      } else {
        const recurringDates = getRecurringDates(newEvent.start, newEvent.end, recurrenceDays, recurrenceFrequency);
        const eventsToCreate = recurringDates.map(date => {
          return {
            title: newEvent.title,
            startDate: date.startDate.toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
            endDate: date.endDate.toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
            color: newEvent.color,
            createdBy: employeeCode,
            type: newEvent.type,
            participants: newEvent.participants.join(',')
          };
        });
        await Promise.all(eventsToCreate.map(event => axios.post(API_BASE_URL, event)));
      }

      await fetchEvents();
      setShowModal(false);
      resetNewEvent();
      closeNotification();
    } catch (err) {
      console.error("Error adding event:", err);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent || !newEvent.title.trim()) return;

    try {
      const response = await axios.put(`${API_BASE_URL}/${selectedEvent.id}`, {
        id: selectedEvent.id,
        title: newEvent.title,
        startDate: newEvent.start.toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
        endDate: newEvent.end.toLocaleString("sv-SE", { hour12: false }).replace(" ", "T"),
        color: newEvent.color,
        type: newEvent.type,
        participants: newEvent.participants.join(',')
      });
      if (response.status === 200 || response.status === 201) {
        await fetchEvents();
        setShowModal(false);
        resetNewEvent();
      }
      closeNotification();
    } catch (err) {
      console.error("Error updating event:", err);
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      const response = await axios.delete(`${API_BASE_URL}/${selectedEvent.id}`);
      if (response.status === 200 || response.status === 201) {
        await fetchEvents();
        setShowModal(false);
        resetNewEvent();
      }
    } catch (err) {
      console.error("Error deleting event:", err);
    }
  };

  // Custom event style
  const eventStyleGetter = (event) => {
    const isAdmin = userRole === "1";
    const isParticipant = event.participants && event.participants.includes(parseInt(employeeCode));
    const isCreator = event.createdBy === employeeCode;

    return {
      style: {
        backgroundColor: event.color,
        borderRadius: '4px',
        opacity: isAdmin ? 1 : (isParticipant || isCreator) ? 0.9 : 0.6,
        color: 'white',
        border: '2px solid white',
        display: 'block',
        fontSize: isMobile ? '10px' : '14px',
        padding: isMobile ? '1px 2px' : '2px 5px'
      }
    };
  };

  const dayPropGetter = (date) => {
    const style = {};
    if (highlightWeekends && isWeekend(date)) {
      style.backgroundColor = '#f5f5f5';
    }
    return { style };
  };

  const closeNotification = () => setNotification(null);

  return (
    <>
      {loading ? (
        <Loader message="Loading..." />
      ) : (
        <div className="">

          <div className="flex flex-col sm:flex-row justify-between items-center mb-3 sm:mb-6 gap-3 sm:gap-4">

            <div className="text-center sm:text-left">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-800 truncate">
                {view === Views.YEAR && format(date, 'yyyy')}
                {view === Views.MONTH && format(date, 'MMMM yyyy')}
                {view === Views.DAY && format(date, 'PPPP')}
              </h2>
            </div>

            <div className="flex-1 flex flex-col sm:flex-row items-center gap-3">

              <div className="flex-1 flex justify-center items-center gap-2">
                <button
                  onClick={() => navigate('PREV')}
                  className="p-2 bg-white hover:bg-gray-50 rounded-lg text-gray-700 shadow-sm border border-gray-200"
                  aria-label="Previous"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>

                <button
                  onClick={() => navigate('TODAY')}
                  className="px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-gray-600 text-sm font-medium shadow-sm border border-gray-200"
                >
                  Today
                </button>

                <button
                  onClick={() => navigate('NEXT')}
                  className="p-2 bg-white hover:bg-gray-50 rounded-lg text-gray-700 shadow-sm border border-gray-200"
                  aria-label="Next"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="flex justify-between items-center w-full sm:w-auto gap-3">

                <div className="flex items-center gap-2">
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={highlightWeekends}
                        onChange={() => setHighlightWeekends(!highlightWeekends)}
                      />
                      <div className={`block w-10 h-6 rounded-full ${highlightWeekends ? 'bg-black' : 'bg-gray-300'}`}></div>
                      <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition ${highlightWeekends ? 'transform translate-x-4' : ''}`}></div>
                    </div>
                    <span className="ml-1 text-sm font-medium text-gray-700">Weekends</span>
                  </label>
                </div>

                {isMobile && (
                  <button
                    onClick={() => setView(view === Views.MONTH ? Views.DAY : Views.MONTH)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
                  >
                    {view === Views.MONTH ? 'Day View' : 'Month View'}
                  </button>
                )}
              </div>
            </div>

            <div className="hidden sm:flex gap-2 w-[180px] justify-end">
              <button
                onClick={() => setView(Views.MONTH)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${view === Views.MONTH ? 'bg-black text-white' : 'bg-white hover:bg-gray-50 text-gray-700'} shadow-sm border border-gray-200`}
              >
                Month
              </button>
              <button
                onClick={() => setView(Views.DAY)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${view === Views.DAY ? 'bg-black text-white' : 'bg-white hover:bg-gray-50 text-gray-700'} shadow-sm border border-gray-200`}
              >
                Day
              </button>
            </div>
          </div>


          {userRole === '1' && (
            <button
              onClick={() => {
                resetNewEvent();
                setSelectedEvent(null);
                resetRecurrence();
                setShowModal(true);
              }}
              className="fixed bottom-6 right-6 bg-black hover:bg-gray-800 text-white rounded-full shadow-lg p-3 flex items-center justify-center transition-transform hover:scale-105"
              style={{ zIndex: 10 }}
              aria-label="Add event"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-7 w-7"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}

          <div className="h-[65vh] sm:h-[70vh] bg-white rounded-lg sm:rounded-xl shadow-sm p-2 sm:p-4 border border-gray-200">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              selectable={userRole === "1"}
              toolbar={false}
              components={{
                event: EventWithIcon
              }}
              formats={{
                dateFormat: isMobile ? 'd' : 'd',
                dayFormat: isMobile ? 'EEEEE' : 'E',
                dayRangeHeaderFormat: formatDateRange,
                timeGutterFormat: isMobile ? 'HH:mm' : 'h:mm a'
              }}
              views={[Views.MONTH, Views.DAY]}
              eventPropGetter={eventStyleGetter}
              dayPropGetter={dayPropGetter}
            />
          </div>

          {/* Event Details Modal */}
          {showDetailsModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
              <div className="bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">
                    Event Details
                  </h3>


                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <h4 className="text-md sm:text-lg font-medium" style={{ color: selectedEvent.color }}>
                        {selectedEvent.title}
                      </h4>
                    </div>

                    {selectedEvent.participants && selectedEvent.participants.length > 0 && (
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">Participants</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedEvent.participants.map(participantId => {
                            const employee = employees.find(e => e.value === participantId);
                            return (
                              <span
                                key={participantId}
                                className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                              >
                                {employee ? employee.label : `ID: ${participantId}`}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">Start</p>
                        <p className="text-sm sm:text-base font-medium">
                        {format(selectedEvent.start, "EEE, dd/MM/yyyy h:mm a")}

                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-gray-500">End</p>
                        <p className="text-sm sm:text-base font-medium">
                        {format(selectedEvent.end, "EEE, dd/MM/yyyy h:mm a")}

                          {/* {format(selectedEvent.end, "MMM d, yyyy h:mm a")} */}
                        </p>
                      </div>
                    </div>

                  </div>

                  <div className="mt-4 sm:mt-6 flex justify-end">
                    <button
                      onClick={() => setShowDetailsModal(false)}
                      className="cancel-btn"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
              <div className="bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4">
                    {selectedEvent ? 'Edit Event' : 'Add New Event'}
                  </h3>
                  {notification && (
                    <div
                      className={`mb-4 p-4 rounded ${notification.type === "error"
                        ? "bg-red-100 text-red-700"
                        : "bg-green-100 text-green-700"
                        }`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{notification.message}</span>
                        <button onClick={closeNotification} className="font-bold text-xl leading-none">
                          ×
                        </button>
                      </div>
                    </div>
                  )}

                  {userRole === "1" && (
                    <div className='mb-4'>
                      <label>Type:</label>
                      <select
                        value={newEvent.type}
                        onChange={(e) =>
                          setNewEvent({ ...newEvent, type: e.target.value })
                        }
                      >
                        <option value="event">Event</option>
                        <option value="meeting">Meeting</option>
                      </select>
                    </div>
                  )}

                  {newEvent.type === "meeting" && (
                    <div className='mb-4'>
                      <label>Participants:</label>

                      <Select
                        isMulti
                        options={allOptions}
                        value={
                          newEvent.participants.length === 0
                            ? []
                            : newEvent.participants.length === employees.length
                              ? [selectAllOption, ...employees]
                              : employees.filter(emp => newEvent.participants.includes(emp.value))
                        }
                        onChange={(selected, actionMeta) => {
                          if (!selected) {
                            setNewEvent({ ...newEvent, participants: [] });
                            return;
                          }

                          const { action, option } = actionMeta || {};

                          if (action === "select-option" && option?.value === selectAllOption.value) {
                            setNewEvent({ ...newEvent, participants: employees.map(e => e.value) });
                            return;
                          }

                          if (action === "deselect-option" && option?.value === selectAllOption.value) {
                            setNewEvent({ ...newEvent, participants: [] });
                            return;
                          }

                          if (action === "clear") {
                            setNewEvent({ ...newEvent, participants: [] });
                            return;
                          }

                          const values = selected
                            .filter(s => s.value !== selectAllOption.value)
                            .map(s => s.value);

                          setNewEvent({ ...newEvent, participants: values });
                        }}
                      />


                    </div>
                  )}

                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Event Title
                      </label>
                      <input
                        type="text"
                        value={newEvent.title}
                        onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                        className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        placeholder="Enter event title"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="datetime-local"
                          value={formatForDateTimeInput(newEvent.start)}
                          onChange={(e) => {
                            setNewEvent({ ...newEvent, start: new Date(e.target.value) });
                          }}
                          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        />

                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                          End Date
                        </label>
                        <input
                          type="datetime-local"
                          value={formatForDateTimeInput(newEvent.end)}
                          onChange={(e) => {
                            setNewEvent({ ...newEvent, end: new Date(e.target.value) });
                          }}
                          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                        />

                      </div>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                        Event Color
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={newEvent.color}
                          onChange={(e) => setNewEvent({ ...newEvent, color: e.target.value })}
                          className="w-20 h-7 p-0 border-0 cursor-pointer"
                        />

                      </div>
                    </div>
                    {!selectedEvent ? (
                      <>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            Repeat on
                          </label>
                          <div className="flex flex-wrap gap-2 sm:gap-3">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                              <label
                                key={day}
                                className={`cursor-pointer select-none px-2 sm:px-3 py-1 rounded-lg border text-xs sm:text-sm ${recurrenceDays.includes(idx)
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50'
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={recurrenceDays.includes(idx)}
                                  onChange={() => toggleRecurrenceDay(idx)}
                                  className="hidden"
                                />
                                {day}
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                            Repeat Frequency
                          </label>
                          <select
                            value={recurrenceFrequency}
                            onChange={(e) => setRecurrenceFrequency(Number(e.target.value))}
                            className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                          >
                            <option value={1}>Every week</option>
                            <option value={2}>Every 2 weeks</option>
                            <option value={3}>Every 3 weeks</option>
                            <option value={4}>Every 4 weeks</option>
                          </select>
                        </div>
                      </>
                    ) : null}
                  </div>


                  <div className="mt-4 sm:mt-6 flex flex-wrap justify-end gap-2 sm:gap-3">
                    <button
                      onClick={() => {
                        setShowModal(false);
                        resetNewEvent();
                      }}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>

                    {selectedEvent ? (
                      <>
                        <button
                          onClick={handleDeleteEvent}
                          className="px-3 sm:px-4 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm sm:text-base font-medium"
                        >
                          Delete
                        </button>
                        <button
                          onClick={handleUpdateEvent}
                          className="btn-black"
                        >
                          Update
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleAddEvent}
                        className="btn-black"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>

  );
};

export default ResponsiveCalendar;